import React from 'react';
import styled from 'styled-components';
import { FiSearch, FiFilter, FiX, FiTarget, FiFlag, FiSend } from 'react-icons/fi';
import theme from '../../styles/theme';

const FiltrosContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

const FiltrosHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[6]};
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FiltrosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FiltroGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const MensagensEtapaFiltros = ({
  filtros,
  onFiltrosChange,
  onBuscar,
  loading,
  funis = [],
  etapas = [],
  onFunilChange,
  loadingEtapas = false,
  origens = []
}) => {
  const handleInputChange = (field, value) => {
    onFiltrosChange({
      ...filtros,
      [field]: value
    });
  };

  const limparFiltros = () => {
    const filtrosLimpos = {
      search: '',
      funil_id: '',
      etapa_id: '',
      canal: '',
      ativa: '',
      page: 1,
      limit: filtros.limit || 10
    };
    onFiltrosChange(filtrosLimpos);
    if (onBuscar) {
      onBuscar(filtrosLimpos);
    }
  };

  const temFiltrosAtivos = Object.values(filtros).some(value => value !== '' && value !== null && value !== undefined);

  return (
    <FiltrosContainer>
      <FiltrosHeader>
        <Title>
          <FiFilter />
          Filtros de Busca
        </Title>
        <Actions>
          <ActionButton onClick={limparFiltros} disabled={!temFiltrosAtivos}>
            <FiX />
            Limpar Filtros
          </ActionButton>
          <ActionButton onClick={onBuscar} disabled={loading}>
            <FiSearch />
            {loading ? 'Buscando...' : 'Buscar'}
          </ActionButton>
        </Actions>
      </FiltrosHeader>

      <FiltrosGrid>
        <FiltroGroup>
          <Label>
            <FiSearch />
            Busca geral
          </Label>
          <Input
            type="text"
            placeholder="Trecho da mensagem, funil ou etapa"
            value={filtros.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
          />
        </FiltroGroup>

        <FiltroGroup>
          <Label>
            <FiSend />
            Canal
          </Label>
          <Select
            value={filtros.canal}
            onChange={(e) => handleInputChange('canal', e.target.value)}
          >
            <option value="">Todos</option>
            {origens.map((origem) => (
              <option key={origem.id} value={origem.nome}>
                {origem.nome}
              </option>
            ))}
          </Select>
        </FiltroGroup>

        <FiltroGroup>
          <Label>
            <FiTarget />
            Funil
          </Label>
          <Select
            value={filtros.funil_id}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange('funil_id', value);
              handleInputChange('etapa_id', '');
              if (onFunilChange) {
                onFunilChange(value);
              }
            }}
          >
            <option value="">Todos os funis</option>
            {funis.map((funil) => (
              <option key={funil.id} value={funil.id}>
                {funil.nome}
              </option>
            ))}
          </Select>
        </FiltroGroup>

        <FiltroGroup>
          <Label>
            <FiFlag />
            Etapa
          </Label>
          <Select
            value={filtros.etapa_id}
            onChange={(e) => handleInputChange('etapa_id', e.target.value)}
            disabled={!filtros.funil_id || loadingEtapas}
          >
            <option value="">
              {filtros.funil_id ? (loadingEtapas ? 'Carregando etapas...' : 'Todas as etapas') : 'Selecione um funil'}
            </option>
            {etapas.map((etapa) => (
              <option key={etapa.id} value={etapa.id}>
                {etapa.ordem ? `#${etapa.ordem} - ` : ''}{etapa.nome}
              </option>
            ))}
          </Select>
        </FiltroGroup>

        <FiltroGroup>
          <Label>Status</Label>
          <Select
            value={filtros.ativa}
            onChange={(e) => handleInputChange('ativa', e.target.value)}
          >
            <option value="">Todas</option>
            <option value="true">Ativas</option>
            <option value="false">Inativas</option>
          </Select>
        </FiltroGroup>
      </FiltrosGrid>
    </FiltrosContainer>
  );
};

export default MensagensEtapaFiltros;

